import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useGetChatsForPickerQuery } from "@/services/tariff-engineering";
import { tokens } from "@/tokens";

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (chatId: string) => void;
  alreadyLinkedIds: string[];
};

const fmtRelativeDate = (s: string | null): string => {
  if (!s) return "—";
  const d = new Date(s);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString();
};

export const ChatPickerDialog: FC<Props> = ({
  open,
  onClose,
  onPick,
  alreadyLinkedIds,
}) => {
  const [search, setSearch] = useState("");

  const { data: chats = [], isFetching } = useGetChatsForPickerQuery(
    { searchKeyword: search, pageSize: 50 },
    { skip: !open }
  );

  // Front-end filter: keep server query loose; let the user narrow further
  // here without round-trips. Also exclude chats already linked.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats.filter((c) => {
      if (alreadyLinkedIds.includes(c._id)) return false;
      if (!q) return true;
      return c.title.toLowerCase().includes(q);
    });
  }, [chats, search, alreadyLinkedIds]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack>
          <Typography variant="h6">Link a chat</Typography>
          <Typography variant="caption" color="text.secondary">
            Attach a conversation so its context feeds into tariff-engineering
            research for this product.
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          {isFetching ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {search ? "No chats match your search." : "No chats to link."}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {filtered.map((c) => (
                <ListItemButton
                  key={c._id}
                  onClick={() => {
                    onPick(c._id);
                    onClose();
                  }}
                  sx={{ alignItems: "flex-start", py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flexWrap: "wrap" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: tokens.fontWeight.semibold }}
                        >
                          {c.title}
                        </Typography>
                        {c.category && (
                          <Chip
                            size="small"
                            label={c.category}
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                            }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {c.totalMessages} message{c.totalMessages === 1 ? "" : "s"} ·{" "}
                        last active {fmtRelativeDate(c.lastActivityAt)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatPickerDialog;
